import { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import {
    generateModuleItems,
    getModule,
    improveItem,
    redeployProject,
    suggestItemImage,
    updateModuleItems
} from "../../services/crmService";

const PAGE_SIZE = 8;

const emptyItem = {
    title: "",
    subtitle: "",
    description: "",
    badge: "",
    imageUrl: "",
    sortOrder: 0,
    isPublished: true
};

function getModuleKey(module) {
    return module?.moduleKey || module?.key || "";
}

function getModuleLabel(module) {
    const key = getModuleKey(module);
    return module?.moduleLabel || module?.label || key || "Module";
}

function getItemId(item, index) {
    return item?.id || item?.itemKey || item?.slug || `${item?.title || "item"}-${index}`;
}

function normalizeItems(data) {
    if (Array.isArray(data)) return data;

    if (Array.isArray(data?.items)) return data.items;
    if (Array.isArray(data?.Items)) return data.Items;
    if (Array.isArray(data?.moduleItems)) return data.moduleItems;
    if (Array.isArray(data?.ModuleItems)) return data.ModuleItems;

    return [];
}

function normalizeModule(data, fallbackModule) {
    return data?.module || data?.Module || data?.definition || data?.Definition || fallbackModule;
}

function text(value) {
    return String(value || "").trim();
}

function buildSavedItems(items) {
    return items.map((item, index) => ({
        ...item,
        title: text(item.title || item.Title),
        subtitle: text(item.subtitle || item.Subtitle),
        description: text(item.description || item.Description),
        badge: text(item.badge || item.Badge),
        imageUrl: text(item.imageUrl || item.ImageUrl),
        sortOrder: Number(item.sortOrder ?? item.SortOrder ?? index),
        isPublished: item.isPublished ?? item.IsPublished ?? true
    }));
}

function ModuleItemForm({ value, saving, onChange, onCancel, onSave, title }) {
    const update = (key, nextValue) => {
        onChange({
            ...value,
            [key]: nextValue
        });
    };

    return (
        <div className="module-item-modal-backdrop">
            <div className="module-item-editor-modal">
                <div className="module-item-editor-head">
                    <div>
                        <span className="project-kicker">Item Editor</span>
                        <h2>{title}</h2>
                        <p>Edit content shown on generated pages and CRM module sections.</p>
                    </div>

                    <button
                        type="button"
                        className="account-modal-close"
                        onClick={onCancel}
                        disabled={saving}
                    >
                        ×
                    </button>
                </div>

                <div className="module-item-editor-form">
                    <label>
                        <span>Title</span>
                        <input
                            value={value.title || ""}
                            onChange={(e) => update("title", e.target.value)}
                            placeholder="Item title"
                            autoFocus
                        />
                    </label>

                    <label>
                        <span>Subtitle</span>
                        <input
                            value={value.subtitle || ""}
                            onChange={(e) => update("subtitle", e.target.value)}
                            placeholder="Short subtitle"
                        />
                    </label>

                    <label>
                        <span>Badge</span>
                        <input
                            value={value.badge || ""}
                            onChange={(e) => update("badge", e.target.value)}
                            placeholder="Best Seller, Popular, Featured..."
                        />
                    </label>

                    <label>
                        <span>Image URL</span>
                        <input
                            value={value.imageUrl || ""}
                            onChange={(e) => update("imageUrl", e.target.value)}
                            placeholder="https://..."
                        />
                    </label>

                    <label className="module-item-editor-wide">
                        <span>Description</span>
                        <textarea
                            value={value.description || ""}
                            onChange={(e) => update("description", e.target.value)}
                            placeholder="Write a clear, customer-facing description."
                            rows={5}
                        />
                    </label>

                    <label className="module-item-editor-check">
                        <input
                            type="checkbox"
                            checked={value.isPublished !== false}
                            onChange={(e) => update("isPublished", e.target.checked)}
                        />
                        <span>Published on website</span>
                    </label>
                </div>

                <div className="module-item-editor-actions">
                    <button
                        type="button"
                        className="project-action-btn secondary"
                        onClick={onCancel}
                        disabled={saving}
                    >
                        Cancel
                    </button>

                    <button
                        type="button"
                        className="project-action-btn primary"
                        onClick={onSave}
                        disabled={saving || !text(value.title)}
                    >
                        {saving ? "Saving..." : "Save Item"}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default function ModuleItemsModal({
    projectId,
    module,
    open,
    onClose,
    onChanged
}) {
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [aiLoading, setAiLoading] = useState(false);
    const [redeploying, setRedeploying] = useState(false);

    const [loadedModule, setLoadedModule] = useState(module);
    const [items, setItems] = useState([]);

    const [search, setSearch] = useState("");
    const [page, setPage] = useState(1);

    const [editorOpen, setEditorOpen] = useState(false);
    const [editingIndex, setEditingIndex] = useState(-1);
    const [editingItem, setEditingItem] = useState(emptyItem);

    const moduleKey = getModuleKey(loadedModule || module);
    const moduleLabel = getModuleLabel(loadedModule || module);

    const loadModuleItems = async () => {
        if (!projectId || !moduleKey) return;

        try {
            setLoading(true);

            const data = await getModule(projectId, moduleKey);
            setLoadedModule(normalizeModule(data, module));
            setItems(normalizeItems(data));
            setPage(1);
        } catch (error) {
            console.error(error);
            toast.error("Failed to load module items.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (open) {
            setLoadedModule(module);
            loadModuleItems();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, projectId, moduleKey]);

    const filteredItems = useMemo(() => {
        const query = search.trim().toLowerCase();

        if (!query) return items;

        return items.filter((item) => {
            return [
                item.title || item.Title,
                item.subtitle || item.Subtitle,
                item.description || item.Description,
                item.badge || item.Badge,
                item.slug || item.Slug
            ]
                .filter(Boolean)
                .join(" ")
                .toLowerCase()
                .includes(query);
        });
    }, [items, search]);

    const pageCount = Math.max(1, Math.ceil(filteredItems.length / PAGE_SIZE));

    const pageItems = useMemo(() => {
        const safePage = Math.min(page, pageCount);
        const start = (safePage - 1) * PAGE_SIZE;

        return filteredItems.slice(start, start + PAGE_SIZE);
    }, [filteredItems, page, pageCount]);

    const saveItems = async (nextItems, successMessage = "Module items saved.") => {
        try {
            setSaving(true);

            const saved = buildSavedItems(nextItems);
            await updateModuleItems(projectId, moduleKey, saved, { publish: true });

            setItems(saved);
            toast.success(successMessage);
            onChanged?.();
        } catch (error) {
            console.error(error);
            toast.error("Failed to save module items.");
        } finally {
            setSaving(false);
        }
    };

    const openAddItem = () => {
        setEditingIndex(-1);
        setEditingItem({
            ...emptyItem,
            sortOrder: items.length
        });
        setEditorOpen(true);
    };

    const openEditItem = (item) => {
        const index = items.findIndex((x, idx) => getItemId(x, idx) === getItemId(item, idx));

        setEditingIndex(index);
        setEditingItem({
            ...emptyItem,
            ...item,
            title: item.title || item.Title || "",
            subtitle: item.subtitle || item.Subtitle || "",
            description: item.description || item.Description || "",
            badge: item.badge || item.Badge || "",
            imageUrl: item.imageUrl || item.ImageUrl || "",
            sortOrder: item.sortOrder ?? item.SortOrder ?? index,
            isPublished: item.isPublished ?? item.IsPublished ?? true
        });
        setEditorOpen(true);
    };

    const handleSaveEditor = async () => {
        const nextItems = [...items];

        if (editingIndex >= 0) {
            nextItems[editingIndex] = {
                ...nextItems[editingIndex],
                ...editingItem
            };
        } else {
            nextItems.push({
                ...editingItem,
                itemKey: editingItem.itemKey || `manual-${Date.now()}`,
                sourceType: "crm-user",
                isAiGenerated: false,
                isUserEdited: true
            });
        }

        await saveItems(nextItems, editingIndex >= 0 ? "Item updated." : "Item added.");
        setEditorOpen(false);
    };

    const handleTogglePublished = async (item) => {
        const id = getItemId(item, 0);

        const nextItems = items.map((x, index) => {
            if (getItemId(x, index) !== id) return x;

            return {
                ...x,
                isPublished: !(x.isPublished ?? x.IsPublished ?? true)
            };
        });

        await saveItems(nextItems, "Publish status updated.");
    };

    const handleDeleteItem = async (item) => {
        const ok = window.confirm("Remove this item from the module?");
        if (!ok) return;

        const id = getItemId(item, 0);
        const nextItems = items.filter((x, index) => getItemId(x, index) !== id);

        await saveItems(nextItems, "Item removed.");
    };

    const handleImprove = async (item) => {
        try {
            setAiLoading(true);

            const improved = await improveItem(projectId, moduleKey, item);

            if (!improved) {
                toast.error("AI did not return an improved item.");
                return;
            }

            const id = getItemId(item, 0);
            const nextItems = items.map((x, index) => {
                if (getItemId(x, index) !== id) return x;

                return {
                    ...x,
                    ...improved,
                    isUserEdited: true
                };
            });

            await saveItems(nextItems, "AI improvement applied.");
        } catch (error) {
            console.error(error);
            toast.error("Failed to improve item.");
        } finally {
            setAiLoading(false);
        }
    };

    const handleSuggestImage = async (item) => {
        try {
            setAiLoading(true);

            const result = await suggestItemImage(projectId, moduleKey, item);
            const imageUrl =
                result?.imageUrl ||
                result?.url ||
                result?.suggestedImageUrl ||
                result?.ImageUrl ||
                "";

            if (!imageUrl) {
                toast.error("No image suggestion returned.");
                return;
            }

            const id = getItemId(item, 0);
            const nextItems = items.map((x, index) => {
                if (getItemId(x, index) !== id) return x;

                return {
                    ...x,
                    imageUrl,
                    isUserEdited: true
                };
            });

            await saveItems(nextItems, "Image suggestion applied.");
        } catch (error) {
            console.error(error);
            toast.error("Failed to suggest image.");
        } finally {
            setAiLoading(false);
        }
    };

    const handleGenerateItems = async () => {
        try {
            setAiLoading(true);

            await generateModuleItems(projectId, moduleKey, {
                replaceExisting: false
            });

            toast.success("AI items generated.");
            await loadModuleItems();
            onChanged?.();
        } catch (error) {
            console.error(error);
            toast.error("Failed to generate AI items.");
        } finally {
            setAiLoading(false);
        }
    };

    const handleRedeploy = async () => {
        try {
            setRedeploying(true);

            await redeployProject(projectId, moduleKey);
            toast.success("Redeploy queued.");
        } catch (error) {
            console.error(error);
            toast.error("Failed to queue redeploy.");
        } finally {
            setRedeploying(false);
        }
    };

    if (!open) return null;

    return (
        <div className="module-items-modal-backdrop">
            <div className="module-items-modal">
                <div className="module-items-modal-head">
                    <div>
                        <span className="project-kicker">Module Manager</span>
                        <h2>{moduleLabel}</h2>
                        <p>
                            View, add, edit, improve, publish, and redeploy content for this module.
                        </p>
                    </div>

                    <button
                        type="button"
                        className="account-modal-close"
                        onClick={onClose}
                        disabled={saving || aiLoading}
                    >
                        ×
                    </button>
                </div>

                <div className="module-items-toolbar">
                    <div className="project-search-box">
                        <span>Search Items</span>
                        <input
                            value={search}
                            onChange={(e) => {
                                setSearch(e.target.value);
                                setPage(1);
                            }}
                            placeholder="Search title, badge, description..."
                        />
                    </div>

                    <div className="module-items-toolbar-actions">
                        <button
                            type="button"
                            className="project-action-btn secondary compact"
                            onClick={loadModuleItems}
                            disabled={loading}
                        >
                            {loading ? "Loading..." : "Refresh"}
                        </button>

                        <button
                            type="button"
                            className="project-action-btn secondary compact"
                            onClick={handleGenerateItems}
                            disabled={aiLoading}
                        >
                            {aiLoading ? "AI working..." : "AI Generate"}
                        </button>

                        <button
                            type="button"
                            className="project-action-btn primary compact"
                            onClick={openAddItem}
                            disabled={saving}
                        >
                            Add Item
                        </button>
                    </div>
                </div>

                <div className="module-items-summary">
                    <span>{items.length} total items</span>
                    <span>{items.filter((x) => x.isPublished ?? x.IsPublished ?? true).length} published</span>
                    <span>{filteredItems.length} matching</span>
                </div>

                {loading ? (
                    <div className="project-loading-card">Loading module items...</div>
                ) : items.length === 0 ? (
                    <div className="module-items-empty">
                        <strong>No items found</strong>
                        <span>Add a manual item or use AI Generate to create suggested content.</span>
                        <button
                            type="button"
                            className="project-action-btn primary"
                            onClick={openAddItem}
                        >
                            Add First Item
                        </button>
                    </div>
                ) : (
                    <>
                        <div className="module-items-table-wrap">
                            <table className="module-items-table">
                                <thead>
                                    <tr>
                                        <th>Item</th>
                                        <th>Status</th>
                                        <th>Image</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>

                                <tbody>
                                    {pageItems.map((item, index) => {
                                        const published = item.isPublished ?? item.IsPublished ?? true;
                                        const title = item.title || item.Title || "Untitled item";
                                        const subtitle = item.subtitle || item.Subtitle || item.badge || item.Badge || "";
                                        const description = item.description || item.Description || "";
                                        const imageUrl = item.imageUrl || item.ImageUrl || "";

                                        return (
                                            <tr key={getItemId(item, index)}>
                                                <td>
                                                    <div className="module-item-title-cell">
                                                        <strong>{title}</strong>
                                                        <span>{subtitle || "No subtitle"}</span>
                                                        <small>{description || "No description yet."}</small>
                                                    </div>
                                                </td>

                                                <td>
                                                    <button
                                                        type="button"
                                                        className={`module-state ${published ? "on" : "off"}`}
                                                        onClick={() => handleTogglePublished(item)}
                                                        disabled={saving}
                                                    >
                                                        {published ? "Published" : "Draft"}
                                                    </button>
                                                </td>

                                                <td>
                                                    {imageUrl ? (
                                                        <img
                                                            src={imageUrl}
                                                            alt={title}
                                                            className="module-item-thumb"
                                                        />
                                                    ) : (
                                                        <span className="module-item-no-image">No image</span>
                                                    )}
                                                </td>

                                                <td>
                                                    <div className="module-row-actions">
                                                        <button
                                                            type="button"
                                                            className="project-action-btn compact secondary"
                                                            onClick={() => openEditItem(item)}
                                                        >
                                                            Edit
                                                        </button>

                                                        <button
                                                            type="button"
                                                            className="project-action-btn compact secondary"
                                                            onClick={() => handleImprove(item)}
                                                            disabled={aiLoading}
                                                        >
                                                            Improve
                                                        </button>

                                                        <button
                                                            type="button"
                                                            className="project-action-btn compact secondary"
                                                            onClick={() => handleSuggestImage(item)}
                                                            disabled={aiLoading}
                                                        >
                                                            Image
                                                        </button>

                                                        <button
                                                            type="button"
                                                            className="project-action-btn compact danger"
                                                            onClick={() => handleDeleteItem(item)}
                                                            disabled={saving}
                                                        >
                                                            Delete
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>

                        <div className="module-items-mobile-list">
                            {pageItems.map((item, index) => {
                                const title = item.title || item.Title || "Untitled item";
                                const subtitle = item.subtitle || item.Subtitle || item.badge || item.Badge || "";
                                const description = item.description || item.Description || "";
                                const imageUrl = item.imageUrl || item.ImageUrl || "";
                                const published = item.isPublished ?? item.IsPublished ?? true;

                                return (
                                    <article key={getItemId(item, index)} className="module-mobile-item-card">
                                        <div className="module-mobile-item-top">
                                            {imageUrl ? (
                                                <img src={imageUrl} alt={title} />
                                            ) : (
                                                <div>No image</div>
                                            )}

                                            <div>
                                                <strong>{title}</strong>
                                                <span>{subtitle || "No subtitle"}</span>
                                            </div>
                                        </div>

                                        <p>{description || "No description yet."}</p>

                                        <div className="module-mobile-item-actions">
                                            <button
                                                type="button"
                                                className={`module-state ${published ? "on" : "off"}`}
                                                onClick={() => handleTogglePublished(item)}
                                            >
                                                {published ? "Published" : "Draft"}
                                            </button>

                                            <button type="button" onClick={() => openEditItem(item)}>Edit</button>
                                            <button type="button" onClick={() => handleImprove(item)}>Improve</button>
                                            <button type="button" onClick={() => handleSuggestImage(item)}>Image</button>
                                        </div>
                                    </article>
                                );
                            })}
                        </div>

                        <div className="module-items-pagination">
                            <button
                                type="button"
                                className="project-action-btn compact secondary"
                                onClick={() => setPage((x) => Math.max(1, x - 1))}
                                disabled={page <= 1}
                            >
                                Prev
                            </button>

                            <span>
                                Page {page} of {pageCount}
                            </span>

                            <button
                                type="button"
                                className="project-action-btn compact secondary"
                                onClick={() => setPage((x) => Math.min(pageCount, x + 1))}
                                disabled={page >= pageCount}
                            >
                                Next
                            </button>
                        </div>
                    </>
                )}

                <div className="module-items-modal-footer">
                    <button
                        type="button"
                        className="project-action-btn secondary"
                        onClick={onClose}
                    >
                        Close
                    </button>

                    <button
                        type="button"
                        className="project-action-btn primary"
                        onClick={handleRedeploy}
                        disabled={redeploying}
                    >
                        {redeploying ? "Queueing..." : "Redeploy Website"}
                    </button>
                </div>
            </div>

            {editorOpen && (
                <ModuleItemForm
                    title={editingIndex >= 0 ? "Edit Item" : "Add Item"}
                    value={editingItem}
                    saving={saving}
                    onChange={setEditingItem}
                    onCancel={() => setEditorOpen(false)}
                    onSave={handleSaveEditor}
                />
            )}
        </div>
    );
}